import { DateTime } from 'luxon';

export type MemberDirectoryType = 'members' | 'visitors' | 'firstTimers';
export type MemberDirectoryStatus = MemberDirectoryType | 'inactive';
export type MembersLifecycleFilter = 'any' | 'active' | 'inactive';
export type MembersSort = 'name' | 'joinedAt' | 'createdAt';
export type MembersAppFilter = 'any' | 'yes' | 'no';

/** @deprecated Use MemberDirectoryStatus[] — kept for insight deep links during migration. */
export type MembersView = 'all' | 'members' | 'visitors' | 'firstTimers' | 'inactive';

export const MEMBER_DIRECTORY_TYPE_OPTIONS: MemberDirectoryType[] = [
  'members',
  'visitors',
  'firstTimers',
];

export const MEMBER_DIRECTORY_STATUS_OPTIONS: MemberDirectoryStatus[] = [
  ...MEMBER_DIRECTORY_TYPE_OPTIONS,
  'inactive',
];

export const DEFAULT_MEMBER_DIRECTORY_STATUSES: MemberDirectoryType[] = [
  'members',
  'visitors',
  'firstTimers',
];

export const MEMBERS_LIFECYCLE_FILTER_OPTIONS: MembersLifecycleFilter[] = [
  'any',
  'active',
  'inactive',
];

export interface MembersListState {
  page: number;
  pageSize: number;
  q: string;
  statuses: MemberDirectoryStatus[];
  lifecycle: MembersLifecycleFilter;
  sort: MembersSort;
  departmentId: string | null;
  unitId: string | null;
  gender: 'female' | 'male' | 'other' | 'prefer_not_to_say' | null;
  letter: string | null;
  joinedFrom: string | null;
  joinedTo: string | null;
  app: MembersAppFilter;
  selectedId: string | null;
}

export const DEFAULT_MEMBERS_LIST_STATE: MembersListState = {
  page: 1,
  pageSize: 25,
  q: '',
  statuses: [],
  lifecycle: 'any',
  sort: 'name',
  departmentId: null,
  unitId: null,
  gender: null,
  letter: null,
  joinedFrom: null,
  joinedTo: null,
  app: 'any',
  selectedId: null,
};

const SORT_VALUES: MembersSort[] = ['name', 'joinedAt', 'createdAt'];
const GENDER_VALUES = ['female', 'male', 'other', 'prefer_not_to_say'] as const;

const isMemberDirectoryType = (value: string): value is MemberDirectoryType =>
  MEMBER_DIRECTORY_TYPE_OPTIONS.includes(value as MemberDirectoryType);

const isMemberDirectoryStatus = (value: string): value is MemberDirectoryStatus =>
  MEMBER_DIRECTORY_STATUS_OPTIONS.includes(value as MemberDirectoryStatus);

const directoryTypesFromStatuses = (statuses: MemberDirectoryStatus[]): MemberDirectoryType[] =>
  statuses.filter(isMemberDirectoryType);

export const memberDirectoryTypesFromStatuses = directoryTypesFromStatuses;

const isExplicitDefaultDirectoryStatuses = (statuses: MemberDirectoryStatus[]): boolean => {
  const types = directoryTypesFromStatuses(statuses);
  if (types.length !== DEFAULT_MEMBER_DIRECTORY_STATUSES.length) return false;
  const sorted = [...types].sort();
  const expected = [...DEFAULT_MEMBER_DIRECTORY_STATUSES].sort();
  return sorted.every((value, index) => value === expected[index]);
};

/** Same people as the implicit default list — used for API query omission only. */
export const isDefaultDirectoryStatuses = (statuses: MemberDirectoryStatus[]): boolean =>
  (statuses.length === 0 || isExplicitDefaultDirectoryStatuses(statuses)) &&
  !statuses.includes('inactive');

export const normalizeDirectoryStatuses = (
  statuses: MemberDirectoryStatus[],
): MemberDirectoryStatus[] => [...new Set(statuses.filter(isMemberDirectoryStatus))];

/** @deprecated Use normalizeDirectoryStatuses */
export const normalizeDirectoryTypes = (statuses: MemberDirectoryType[]): MemberDirectoryType[] =>
  normalizeDirectoryStatuses(statuses).filter(isMemberDirectoryType);

export const membershipStatusFromStatuses = (
  statuses: MemberDirectoryStatus[],
  lifecycle: MembersLifecycleFilter = 'any',
): MembersLifecycleFilter => {
  if (lifecycle === 'active') return 'active';
  if (statuses.includes('inactive') || lifecycle === 'inactive') return 'inactive';
  return 'any';
};

export const statusesWithLifecycle = (
  types: MemberDirectoryType[],
  lifecycle: MembersLifecycleFilter,
): MemberDirectoryStatus[] => applyMembershipStatus(types, lifecycle);

export const applyMembershipStatus = (
  statuses: MemberDirectoryStatus[],
  lifecycle: MembersLifecycleFilter,
): MemberDirectoryStatus[] => {
  const types = directoryTypesFromStatuses(statuses);
  if (lifecycle === 'inactive') {
    return types.length > 0 ? [...types, 'inactive'] : ['inactive'];
  }
  return types;
};

const statusesFromLegacyView = (view: string | null): MemberDirectoryStatus[] | null => {
  switch (view) {
    case 'members':
      return ['members'];
    case 'visitors':
      return ['visitors'];
    case 'firstTimers':
      return ['firstTimers'];
    case 'inactive':
      return [];
    case 'all':
      return [];
    default:
      return null;
  }
};

const parseLifecycleParam = (
  params: URLSearchParams,
  statuses: MemberDirectoryStatus[],
): MembersLifecycleFilter => {
  const lifecycleParam = params.get('lifecycle');
  if (
    lifecycleParam === 'active' ||
    lifecycleParam === 'inactive' ||
    lifecycleParam === 'any'
  ) {
    return lifecycleParam;
  }
  if (params.get('view') === 'inactive') {
    return 'inactive';
  }
  const statusParam = params.get('status');
  if (
    statusParam
      ?.split(',')
      .map((part) => part.trim())
      .includes('inactive')
  ) {
    return 'inactive';
  }
  if (statuses.includes('inactive')) {
    return 'inactive';
  }
  return 'any';
};

const parseStatusesParam = (params: URLSearchParams): MemberDirectoryStatus[] => {
  const legacy = statusesFromLegacyView(params.get('view'));
  if (legacy) return legacy.filter((status) => status !== 'inactive');

  const statusParam = params.get('status');
  if (!statusParam) return [];

  return normalizeDirectoryStatuses(
    statusParam
      .split(',')
      .map((part) => part.trim())
      .filter(isMemberDirectoryStatus)
      .filter((part): part is MemberDirectoryType => part !== 'inactive'),
  );
};

export const parseMembersListState = (params: URLSearchParams): MembersListState => {
  const sortParam = params.get('sort');
  const genderParam = params.get('gender');
  const appParam = params.get('app');

  const statuses = parseStatusesParam(params);

  return {
    page: Math.max(1, Number(params.get('page') ?? 1) || 1),
    pageSize: Math.min(100, Math.max(1, Number(params.get('pageSize') ?? 25) || 25)),
    q: params.get('q') ?? '',
    statuses,
    lifecycle: parseLifecycleParam(params, statuses),
    sort: SORT_VALUES.includes(sortParam as MembersSort) ? (sortParam as MembersSort) : 'name',
    departmentId: params.get('departmentId') || null,
    unitId: params.get('unitId') || null,
    gender: GENDER_VALUES.includes(genderParam as (typeof GENDER_VALUES)[number])
      ? (genderParam as MembersListState['gender'])
      : null,
    letter: params.get('letter') || null,
    joinedFrom: params.get('joinedFrom') || null,
    joinedTo: params.get('joinedTo') || null,
    app: appParam === 'yes' || appParam === 'no' ? appParam : 'any',
    selectedId: params.get('selected') || null,
  };
};

export const serializeMembersListState = (
  state: MembersListState,
  options?: { includeSelected?: boolean },
): URLSearchParams => {
  const params = new URLSearchParams();
  if (state.page > 1) params.set('page', String(state.page));
  if (state.pageSize !== DEFAULT_MEMBERS_LIST_STATE.pageSize) {
    params.set('pageSize', String(state.pageSize));
  }
  if (state.q.trim()) params.set('q', state.q.trim());
  if (state.statuses.length > 0) {
    params.set('status', state.statuses.join(','));
  }
  if (state.lifecycle !== 'any') {
    params.set('lifecycle', state.lifecycle);
  }
  if (state.sort !== 'name') params.set('sort', state.sort);
  if (state.departmentId) params.set('departmentId', state.departmentId);
  if (state.unitId) params.set('unitId', state.unitId);
  if (state.gender) params.set('gender', state.gender);
  if (state.letter) params.set('letter', state.letter);
  if (state.joinedFrom) params.set('joinedFrom', state.joinedFrom);
  if (state.joinedTo) params.set('joinedTo', state.joinedTo);
  if (state.app !== 'any') params.set('app', state.app);
  if (options?.includeSelected !== false && state.selectedId) {
    params.set('selected', state.selectedId);
  }
  return params;
};

export const membersListSearchString = (
  state: MembersListState,
  options?: { includeSelected?: boolean },
): string => serializeMembersListState(state, options).toString();

export const membersListPath = (
  state: MembersListState,
  options?: { includeSelected?: boolean },
): string => {
  const search = membersListSearchString(state, options);
  return search ? `/people?${search}` : '/people';
};

export const memberDetailPath = (memberId: string, listState: MembersListState): string => {
  const params = serializeMembersListState(listState, { includeSelected: false });
  const search = params.toString();
  return search ? `/people/${memberId}?${search}` : `/people/${memberId}`;
};

export const membersListToApiQuery = (state: MembersListState): Record<string, string> => {
  const query: Record<string, string> = {
    page: String(state.page),
    pageSize: String(state.pageSize),
  };
  if (state.q.trim()) query.q = state.q.trim();
  const directoryStatus = applyMembershipStatus(state.statuses, state.lifecycle).join(',');
  if (directoryStatus) {
    query.directoryStatus = directoryStatus;
  }
  if (state.sort !== 'name') query.sort = state.sort;
  if (state.departmentId) query.departmentId = state.departmentId;
  if (state.unitId) query.unitId = state.unitId;
  if (state.gender) query.gender = state.gender;
  if (state.letter) query.lastNamePrefix = state.letter;
  if (state.joinedFrom) query.joinedFrom = state.joinedFrom;
  if (state.joinedTo) query.joinedTo = state.joinedTo;
  if (state.app === 'yes') query.appConnected = 'true';
  if (state.app === 'no') query.appConnected = 'false';
  return query;
};

export const churchMonthDateRange = (timezone: string): { from: string; to: string } => {
  const now = DateTime.now().setZone(timezone);
  return {
    from: now.startOf('month').toISODate() ?? '',
    to: now.endOf('month').toISODate() ?? '',
  };
};

export const membersListStateEquals = (left: MembersListState, right: MembersListState): boolean =>
  membersListSearchString(left, { includeSelected: true }) ===
  membersListSearchString(right, { includeSelected: true });

export const countActiveFilters = (state: MembersListState): number => {
  let count = 0;
  if (state.departmentId) count += 1;
  if (state.unitId) count += 1;
  if (state.gender) count += 1;
  if (state.letter) count += 1;
  if (state.joinedFrom || state.joinedTo) count += 1;
  if (state.app !== 'any') count += 1;
  if (state.sort !== 'name') count += 1;
  return count;
};

export const hasActiveListFilters = (state: MembersListState): boolean =>
  state.statuses.length > 0 ||
  state.lifecycle !== 'any' ||
  state.gender !== null ||
  state.departmentId !== null ||
  state.unitId !== null ||
  state.letter !== null ||
  state.joinedFrom !== null ||
  state.joinedTo !== null ||
  state.app !== 'any' ||
  state.sort !== 'name';
